import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Navbar } from '../components/layout/Navbar';
import { useDocsI18n } from '../i18n';
import { Search, Book, Code, ArrowRight, Zap, FileText, Layers, BookOpen } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomePage() {
    const { t } = useDocsI18n();
    const [projects, setProjects] = useState([]);
    const [stats, setStats] = useState({ projects: 0, documentations: 0, categories: 0 });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [projectsRes, statsRes] = await Promise.all([
                axios.get(`${API}/projects`),
                axios.get(`${API}/stats/public`).catch(() => ({ data: null })),
            ]);
            setProjects(projectsRes.data);
            if (statsRes.data) setStats(statsRes.data);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background overflow-x-hidden">
            <div className="titanv-glow-blue" />
            <div className="titanv-glow-pink" />
            <div className="noise-overlay" />

            <Navbar />

            {/* Hero Section */}
            <section className="relative pt-40 pb-24 px-6 z-10">
                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-16 items-center">
                    {/* Left: Text */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <Badge
                            variant="outline"
                            className="mb-6 font-mono text-xs uppercase tracking-widest border-primary/40 bg-primary/5 text-primary rounded-full px-3 py-1"
                            data-testid="titanv-badge"
                        >
                            <span className="w-1.5 h-1.5 rounded-full bg-primary mr-2 animate-pulse" />
                            {t('badge')}
                        </Badge>

                        <h1 className="text-5xl sm:text-6xl lg:text-7xl font-display font-black tracking-tight leading-[1.05] mb-6">
                            <span className="block text-foreground">{t('hero1')}</span>
                            <span className="block titanv-gradient-text">{t('hero2')}</span>
                        </h1>

                        <p className="text-base sm:text-lg text-muted-foreground max-w-xl mb-10 leading-relaxed">
                            {t('lead')}
                        </p>

                        <div className="flex flex-col sm:flex-row gap-3 mb-14">
                            <Button
                                asChild
                                size="lg"
                                className="titanv-gradient text-white font-semibold border-0 hover:opacity-90 hover:scale-[1.02] transition-all shadow-lg shadow-primary/20 rounded-xl gap-2"
                                data-testid="browse-docs-btn"
                            >
                                <Link to="/search">
                                    <Search className="w-4 h-4" />
                                    {t('browse')}
                                </Link>
                            </Button>
                            <Button
                                asChild
                                variant="outline"
                                size="lg"
                                className="rounded-xl border-border/70 bg-card/40 backdrop-blur hover:border-primary/60 hover:bg-card/70 gap-2"
                                data-testid="view-projects-btn"
                            >
                                <a href="#projects">
                                    <Book className="w-4 h-4" />
                                    {t('allProjects')}
                                    <ArrowRight className="w-4 h-4 ml-1" />
                                </a>
                            </Button>
                        </div>

                        {/* Live stats */}
                        <div className="flex items-start gap-10">
                            {[
                                { value: stats.projects, label: t('statProjects') },
                                { value: stats.documentations, label: t('statGuides') },
                                { value: stats.categories, label: t('statCategories') },
                            ].map(stat => (
                                <div key={stat.label} data-testid={`stat-${stat.label}`}>
                                    <div className="text-3xl sm:text-4xl font-display font-black text-foreground">
                                        {stat.value}
                                    </div>
                                    <div className="text-xs text-muted-foreground uppercase tracking-wider mt-1">
                                        {stat.label}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </motion.div>

                    {/* Right: Code snippet card (real doc example) */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.15 }}
                        className="relative"
                    >
                        <div className="absolute -inset-4 titanv-gradient opacity-20 blur-3xl rounded-3xl pointer-events-none" />
                        <div className="relative rounded-2xl border border-border/70 bg-card/80 backdrop-blur-xl shadow-2xl overflow-hidden">
                            <div className="flex items-center gap-2 px-4 py-3 border-b border-border/50 bg-background/40">
                                <span className="w-3 h-3 rounded-full bg-red-500/80" />
                                <span className="w-3 h-3 rounded-full bg-yellow-500/80" />
                                <span className="w-3 h-3 rounded-full bg-green-500/80" />
                                <span className="ml-3 font-mono text-xs text-muted-foreground">tv_garage / config.lua</span>
                                <span className="ml-auto font-mono text-[10px] uppercase tracking-wider text-primary bg-primary/10 px-2 py-0.5 rounded">docs</span>
                            </div>
                            {/* Code body */}
                            <pre className="!m-0 !p-6 !bg-transparent !border-0 font-mono text-xs sm:text-sm leading-relaxed overflow-x-auto">
                                <code>
                                    <span className="text-muted-foreground">--[[  TitanV Garage — Config  ]]--</span>{'\n\n'}
                                    <span className="text-pink-400">Config</span> = {'{}'}{'\n\n'}
                                    <span className="text-muted-foreground">-- Inventory ('auto' erkennt automatisch)</span>{'\n'}
                                    <span className="text-pink-400">Config</span>.<span className="text-blue-300">InventorySystem</span> = <span className="text-green-400">'auto'</span>{'\n\n'}
                                    <span className="text-muted-foreground">-- Garage Einstellungen</span>{'\n'}
                                    <span className="text-pink-400">Config</span>.<span className="text-blue-300">MaxVehicles</span>      = <span className="text-orange-400">25</span>{'\n'}
                                    <span className="text-pink-400">Config</span>.<span className="text-blue-300">SaveOnDisconnect</span> = <span className="text-blue-400">true</span>{'\n'}
                                    <span className="text-pink-400">Config</span>.<span className="text-blue-300">BlipColor</span>        = <span className="text-orange-400">3</span>{'\n\n'}
                                    <span className="text-muted-foreground">-- Discord Webhook</span>{'\n'}
                                    <span className="text-pink-400">Config</span>.<span className="text-blue-300">Discord</span> = {'{'}{'\n'}
                                    {'  '}<span className="text-blue-300">Enabled</span> = <span className="text-blue-400">true</span>,{'\n'}
                                    {'  '}<span className="text-blue-300">BotName</span> = <span className="text-green-400">'TitanV Garage'</span>,{'\n'}
                                    {'}'}{'\n\n'}
                                    <span className="text-green-400">● OK — 200 in 12ms</span>
                                </code>
                            </pre>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Features */}
            <section className="relative py-24 px-6 border-t border-border/50 z-10">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-14">
                        <p className="text-xs font-mono uppercase tracking-widest text-primary mb-3">{t('featuresEyebrow')}</p>
                        <h2 className="text-3xl sm:text-4xl font-display font-bold">
                            {t('featuresTitle')}
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {[
                            {
                                icon: Zap,
                                title: t('featSearch'),
                                description: t('featSearchText'),
                            },
                            {
                                icon: Code,
                                title: t('featCode'),
                                description: t('featCodeText'),
                            },
                            {
                                icon: BookOpen,
                                title: t('featCurrent'),
                                description: t('featCurrentText'),
                            },
                        ].map((feature, index) => (
                            <motion.div
                                key={feature.title}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                            >
                                <div className="relative overflow-hidden rounded-2xl border border-border/60 bg-card/40 backdrop-blur-sm p-6 hover:border-primary/50 transition-all group h-full">
                                    <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                                    <div className="relative">
                                        <div className="w-11 h-11 rounded-xl titanv-gradient flex items-center justify-center mb-4 shadow-lg shadow-primary/20">
                                            <feature.icon className="w-5 h-5 text-white" />
                                        </div>
                                        <h3 className="font-display font-bold text-lg mb-2">{feature.title}</h3>
                                        <p className="text-sm text-muted-foreground leading-relaxed">{feature.description}</p>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Projects */}
            <section id="projects" className="relative py-24 px-6 border-t border-border/50 z-10">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-14">
                        <p className="text-xs font-mono uppercase tracking-widest text-primary mb-3">{t('projectsEyebrow')}</p>
                        <h2 className="text-3xl sm:text-4xl font-display font-bold mb-3">
                            {t('projectsTitle')}
                        </h2>
                        <p className="text-muted-foreground">
                            {t('projectsLead')}
                        </p>
                    </div>

                    {loading ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {[1, 2, 3].map(i => (
                                <div key={i} className="h-48 rounded-2xl bg-muted/50 animate-pulse" />
                            ))}
                        </div>
                    ) : projects.length === 0 ? (
                        <div className="text-center py-16 border border-dashed border-border/60 rounded-2xl bg-card/30">
                            <Book className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                            <h3 className="font-display font-bold text-lg mb-2">{t('emptyTitle')}</h3>
                            <p className="text-muted-foreground text-sm">
                                {t('emptyText')}
                            </p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {projects.map((project, index) => (
                                <motion.div
                                    key={project.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: index * 0.05 }}
                                >
                                    <Link to={`/docs/${project.slug}`} data-testid={`project-card-${project.slug}`}>
                                        <Card className="h-full border-border/60 bg-card/40 backdrop-blur-sm hover:border-primary/60 hover:shadow-xl hover:shadow-primary/10 hover:-translate-y-0.5 transition-all group cursor-pointer rounded-2xl">
                                            <CardHeader>
                                                <div className="flex items-center gap-3 mb-2">
                                                    {project.icon ? (
                                                        <span className="text-3xl">{project.icon}</span>
                                                    ) : (
                                                        <div
                                                            className="w-11 h-11 rounded-xl flex items-center justify-center titanv-gradient shadow-lg shadow-primary/20"
                                                        >
                                                            <Code className="w-5 h-5 text-white" />
                                                        </div>
                                                    )}
                                                    <div>
                                                        <CardTitle className="font-display group-hover:titanv-gradient-text transition-all">
                                                            {project.name}
                                                        </CardTitle>
                                                    </div>
                                                </div>
                                                <CardDescription className="line-clamp-2">
                                                    {project.description}
                                                </CardDescription>
                                            </CardHeader>
                                            <CardContent>
                                                <div className="flex items-center text-sm text-primary font-medium group-hover:translate-x-1 transition-transform">
                                                    {t('openDocs')}
                                                    <ArrowRight className="w-4 h-4 ml-2" />
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </Link>
                                </motion.div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Footer */}
            <footer className="relative py-10 px-6 border-t border-border/50 z-10">
                <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg titanv-gradient flex items-center justify-center font-mono font-bold text-white text-xs">
                            &gt;_
                        </div>
                        <span className="font-display font-bold">TitanV Development</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                        &copy; {new Date().getFullYear()} TitanV Development. Docs powered with care.
                    </p>
                </div>
            </footer>
        </div>
    );
}
